import React, { useState, useEffect } from "react";
import {
  Table,
  Space,
  Button,
  Modal,
  Form,
  Input,
  message,
  Card,
  Divider,
  Descriptions,
  Collapse,
  Alert,
} from "antd";
import {
  EyeOutlined,
  CaretRightOutlined,
  QuestionCircleFilled,
} from "@ant-design/icons";
import httpUtil from "../../utils/httpUtil";
import "./index.css";

const { Panel } = Collapse;

export const Announcement = ({ userInfo }) => {
  const [terms, setTerms] = useState([]);

  // 数据获取相关状态
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  // 详情相关状态
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [currentDetailRecord, setCurrentDetailRecord] = useState(null);

  // 申诉相关状态
  const [appealModalVisible, setAppealModalVisible] = useState(false);
  const [currentAppealRecord, setCurrentAppealRecord] = useState(null);
  const [currentAppealIndicator, setCurrentAppealIndicator] = useState(null);
  const [appealForm] = Form.useForm();

  //获取日期
  function getCurrentTerm() {
    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // 月份0-11转为1-12

    if (month >= 9 || month <= 1) {
      // 第一学期（9月到次年1月）
      // 如果是1月，学年还是上一年
      const academicYear = month <= 1 ? year - 1 : year;
      return `${academicYear}-1`;
    } else if (month >= 2 && month <= 6) {
      // 第二学期（2月到6月）
      return `${year - 1}-2`;
    } else {
      // 7-8月暑假，归为上一学年第二学期
      return `${year - 1}-2`;
    }
  }

  // 获取数据
  const fetchRecords = async () => {
    setLoading(true);
    try {
      const term = getCurrentTerm();
      const params = { student_id: userInfo.username, term };
      httpUtil.getClassEvaluationResults(params).then((res) => {
        if (res.success) {
          if (res.data.count === 0) {
            // message.info("还未进行公示！");
            message.info(res.message);
          } else {
            // 处理数据，确保只显示有评价记录的指标
            const processedResults = res.data.results.map((record) => ({
              ...record,
              details: record.details
                .map((primary) => ({
                  ...primary,
                  details: primary.details.filter(
                    (detail) =>
                      detail.score !== null && detail.score !== undefined
                  ),
                }))
                .filter((primary) => primary.details.length > 0),
            }));
            setRecords(processedResults);
          }
        }
        setLoading(false);
      });
    } catch (error) {
      message.error("获取评价记录失败");
      console.error("获取评价记录错误:", error);
      setLoading(false);
    }
  };

  // 生成学期选项的函数
  const generateTermOptions = (enrollmentDate) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 月份1-12
    const enrollmentYear = new Date(enrollmentDate).getFullYear();
    const terms = [];

    // 生成所有可能的学期（直到当前年份）
    for (let year = enrollmentYear; year <= currentYear - 1; year++) {
      // 第一学期（当年9月1日 - 次年1月31日）
      terms.push({
        term_id: `${year}-1`,
        term_name: `${year}学年第一学期`,
        start_date: `${year}-09-01`,
        end_date: `${year + 1}-01-31`, // 跨年到次年1月
      });

      // 第二学期（次年2月1日 - 次年8月31日）
      terms.push({
        term_id: `${year}-2`,
        term_name: `${year}学年第二学期`,
        start_date: `${year + 1}-02-01`,
        end_date: `${year + 1}-08-31`, // 延长到8月31日
      });
    }
    // console.log("terms",terms);
    // 根据当前日期过滤学期
    if (currentMonth <= 1) {
      // 1月及之前：去掉最后一个学期数据
      return terms.slice(0, -1);
    } else {
      // 2月及之后：返回全部
      return terms;
    }
  };
  //生成评价记录
  const fetchEnrollmentDate = async () => {
    const params = { student_id: userInfo.username };
    setLoading(true);
    try {
      await httpUtil.getEnrollmentDateByStudent(params).then((res) => {
        if (res.success) {
          const enrollmentDate = new Date(res.data.enrollment_date)
            .toISOString()
            .split("T")[0];
          const newterms = generateTermOptions(enrollmentDate);
          setTerms(newterms);
          for (let i = 0; i < newterms.length; i++) {
            const generateParams = {
              student_id: userInfo.username,
              student_name: userInfo.full_name,
              term: newterms[i].term_id,
            };
            httpUtil.generateEvaluationResult(generateParams);
          }
        }
      });
    } catch (error) {
      console.error("获取评价结果失败:", error);
      message.error("获取数据失败，请稍后重试");
    } finally {
      fetchRecords();
    }
  };

  useEffect(() => {
    getCurrentTerm();
    fetchEnrollmentDate();
  }, [userInfo]);

  // 查看详情处理
  const handleViewDetail = (record) => {
    setCurrentDetailRecord(record);
    setDetailModalVisible(true);
  };

  // 打开申诉模态框
  const handleOpenAppeal = (record, indicator = null) => {
    let recordId = null;
    if (indicator) {
      for (const primary of record.details) {
        const found = primary.details.find(
          (d) => d.indicator_id === indicator.indicator_id
        );
        if (found && found.record_id) {
          recordId = found.record_id;
          break;
        }
      }
    }

    if (!recordId && indicator) {
      message.error("未找到对应的评价记录ID");
      return;
    }

    setCurrentAppealRecord({
      ...record,
      record_id: recordId || record.result_id, // 使用record_id或回退到result_id
    });
    setCurrentAppealIndicator(indicator);
    setAppealModalVisible(true);
    appealForm.resetFields();
  };

  //提交申诉之后更新评价结果
  const appealEvaluationRecord = (params) => {
    httpUtil.generateEvaluationResult(params).then((res) => {
      if (res.success) {
        message.success("评价结果已更新");
        // 重新获取最新评价结果
        fetchRecords();
      } else {
        message.error(res.error || "评价结果更新失败");
      }
    });
  };

  // 提交申诉
  const handleAppealSubmit = async () => {
    try {
      setLoading(true);
      const values = await appealForm.validateFields();

      const params = {
        record_id: currentAppealRecord.record_id, // 使用从二级评价中获取的record_id
        student_id: currentAppealRecord.student_id,
        reason: values.reason,
      };
      // console.log(currentAppealRecord);

      const updateParams = {
        student_id: userInfo.username,
        term: currentAppealRecord.term,
        student_name: currentAppealRecord.student_name,
      };
      // console.log(updateParams);

      httpUtil.appealEvaluationRecord(params).then((res) => {
        if (res.success) {
          message.success("申诉提交成功");
          appealEvaluationRecord(updateParams);

          setAppealModalVisible(false);
        } else {
          message.error(res.error || "申诉提交失败");
        }
      });
    } catch (error) {
      console.error("申诉提交错误:", error);
      message.error("申诉提交过程中发生错误");
    } finally {
      setLoading(false);
    }
  };

  // 表格列定义
  const columns = [
    {
      title: "学号",
      dataIndex: "student_id",
      key: "student_id",
      width: 120,
    },
    {
      title: "姓名",
      dataIndex: "student_name",
      key: "student_name",
      width: 100,
    },
    {
      title: "班级",
      dataIndex: "class",
      key: "class",
      width: 80,
    },
    {
      title: "总分",
      dataIndex: "total_score",
      key: "total_score",
      width: 100,
      render: (score) => `${Number(score)?.toFixed(2) ?? "0.00"}分`,
    },
    {
      title: "审核时间",
      dataIndex: "calculation_date",
      key: "calculation_date",
      width: 180,
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "操作",
      key: "action",
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetail(record);
            }}
          >
            详情
          </Button>
        </Space>
      ),
    },
  ];

  // 展开行内容
  const expandedRowRender = (record) => {
    // 预处理数据：过滤掉没有评价记录的二级指标
    const processedDetails = record.details
      .map((primary) => ({
        ...primary,
        details: primary.details.filter(
          (detail) => detail.score !== null && detail.score !== undefined
        ),
      }))
      .filter((primary) => primary.details.length > 0); // 只保留有评价记录的一级指标

    return (
      <div style={{ margin: 0 }}>
        <Collapse
          bordered={false}
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
          className="site-collapse-custom-collapse"
        >
          {processedDetails.map((primary, index) => (
            <Panel
              header={`${primary.name} (${primary.score.toFixed(3)}分, 权重:${(
                primary.weight * 100
              ).toFixed(2)}%)`}
              key={index}
              className="site-collapse-custom-panel"
            >
              <Table
                columns={[
                  {
                    title: "二级指标",
                    dataIndex: "indicator_name",
                    key: "indicator_name",
                  },
                  { title: "备注", dataIndex: "comment", key: "comment" },
                  {
                    title: "得分",
                    dataIndex: "score",
                    key: "score",
                    render: (score, indicator) => (
                      <Space>
                        {`${Number(score).toFixed(3)}分`}
                        <Button
                          type="link"
                          size="small"
                          danger
                          onClick={() =>
                            handleOpenAppeal(record, {
                              indicator_id: indicator.indicator_id,
                              indicator_name: indicator.indicator_name,
                              record_id: indicator.record_id,
                            })
                          }
                        >
                          <QuestionCircleFilled />
                          申诉
                        </Button>
                      </Space>
                    ),
                  },
                ]}
                dataSource={primary.details}
                pagination={false}
                size="small"
                bordered
                rowKey="indicator_id"
              />
            </Panel>
          ))}
        </Collapse>
      </div>
    );
  };

  return (
    <div className="announcement-container">
      {/* 申诉模态框 */}
      <Modal
        title={`申诉 - ${
          currentAppealIndicator
            ? currentAppealIndicator.indicator_name
            : "总分"
        }`}
        open={appealModalVisible}
        onOk={handleAppealSubmit}
        onCancel={() => {
          setAppealModalVisible(false);
          setCurrentAppealIndicator(null);
        }}
        confirmLoading={loading}
        width={600}
        okText={"提交申诉"}
        cancelText={"取消"}
      >
        <Form form={appealForm} layout="vertical">
          <Form.Item label="申诉对象">
            <Input
              value={
                currentAppealIndicator
                  ? `${currentAppealIndicator.indicator_name} (记录ID: ${currentAppealRecord?.record_id})`
                  : `总分 (记录ID: ${currentAppealRecord?.result_id})`
              }
              disabled
            />
          </Form.Item>
          <Form.Item
            name="reason"
            label="申诉理由"
            rules={[{ required: true, message: "请输入申诉理由" }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="请详细说明申诉理由，包括你认为评价不准确的具体原因和相关证据"
            />
          </Form.Item>
        </Form>
        {/* 添加的提示信息 */}
        <Alert
          message="重要提示"
          description="提交申诉后，该评价记录将不再显示在公示窗口中，等待耐心等待评价人处理。"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      </Modal>

      <Card title="评价记录公示" variant={false}>
        <Table
          columns={columns}
          dataSource={records}
          rowKey="result_id"
          loading={loading}
          pagination={false}
          scroll={{ y: "calc(70vh - 100px)", x: "max-content" }}
          style={{ maxHeight: "70vh" }}
          expandable={{
            expandedRowRender,
            expandRowByClick: true,
          }}
        />
      </Card>

      {/* 记录详情模态框 */}
      <Modal
        title="评价记录详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {currentDetailRecord && (
          <>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="学号">
                {currentDetailRecord.student_id}
              </Descriptions.Item>
              <Descriptions.Item label="姓名">
                {currentDetailRecord.student_name}
              </Descriptions.Item>
              <Descriptions.Item label="班级">
                {currentDetailRecord.class}
              </Descriptions.Item>
              <Descriptions.Item label="总分">
                {(Number(currentDetailRecord.total_score) ?? 0).toFixed(2)}分
              </Descriptions.Item>
              <Descriptions.Item label="计算时间">
                {new Date(
                  currentDetailRecord.calculation_date
                ).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">详细评分</Divider>
            <Collapse
              bordered={false}
              expandIcon={({ isActive }) => (
                <CaretRightOutlined rotate={isActive ? 90 : 0} />
              )}
            >
              {currentDetailRecord.details.map((primary, index) => (
                <Panel
                  header={`${primary.name} (${(primary.score ?? 0).toFixed(
                    2
                  )}分)`}
                  key={index}
                >
                  <Table
                    columns={[
                      {
                        title: "二级指标",
                        dataIndex: "indicator_name",
                        key: "indicator_name",
                      },
                      {
                        title: "得分",
                        dataIndex: "score",
                        key: "score",
                        render: (score, indicator) => (
                          <Space>
                            {`${score}分`}
                            <Button
                              type="link"
                              size="small"
                              danger
                              onClick={() =>
                                handleOpenAppeal(currentDetailRecord, {
                                  indicator_id: indicator.indicator_id,
                                  indicator_name: indicator.indicator_name,
                                })
                              }
                            >
                              <QuestionCircleFilled />
                              申诉
                            </Button>
                          </Space>
                        ),
                      },
                    ]}
                    dataSource={primary.details.filter(
                      (d) => d.score !== null && d.score !== undefined
                    )}
                    pagination={false}
                    size="small"
                    bordered
                    rowKey="indicator_id"
                  />
                </Panel>
              ))}
            </Collapse>
          </>
        )}
      </Modal>
    </div>
  );
};
